/* Eagle Leather (EL 2026-10-01): cart stock cap + cart delivery estimator for Pillar.
   Loaded from snippets/el-cart-assets.liquid (cart drawer + /cart). No Pillar core JS is edited.

   1. Stock cap. Each cart line's quantity input carries max = available stock when Shopify tracks inventory and the
      variant stops selling at zero (snippets/el-cart-qty.liquid); Pillar's <quantity-input> already disables "+" at max.
      - Typing a bigger number: clamped to max here (capture listener, runs before Pillar's change handler) and the line
        shows "Only N available in this size" in its aria-live slot. If the clamped value is what the cart already
        holds, the useless /cart/change request is skipped.
      - /cart/change refusals: Pillar's CartItems reads `parsedState.errors` and passes it to setValidity() (native
        bubble) or alert(). Shopify's 422 body carries `description`/`message` and may have no `errors`, which left
        the shopper with "undefined" or a stuck spinner. The fetch wrapper below normalises every refusal so `errors`
        is always real text (description > errors > message, else "Available stock exceeded"), and
        CartItems.prototype.setValidity is replaced so the text shows inline next to the line instead of a popup.
      - If Shopify silently trims a line to the stock it has (200 response, lower quantity), the line's message is
        announced after Pillar re-renders the cart.
   2. Delivery estimator ([data-el-cart-zip], snippets/el-cart-shipping.liquid). Date math: window.elShipMath and
      ZIP -> ground transit days: window.elZipGroundDays, both from assets/el-shipping.js (loaded on every page from
      snippets/el-head.liquid). EL 2026-10-02: data-src now points at el-shipping.js and is only a fallback if that
      file had not loaded (it used to lazy-load the whole product-page script just for the ZIP table).
      A ZIP prefilled from the customer's address (data-prefill) or entered earlier this
      session is estimated automatically: at once when el-shipping.js is present (no request, pure date math),
      otherwise once the box is visible (IntersectionObserver) via the data-src fallback.
      Pillar replaces the drawer/cart markup after every change; a MutationObserver re-hydrates
      the new box from memory without another request. */
(function () {
  'use strict';
  if (window.__elCart) return;
  window.__elCart = true;

  var FALLBACK = 'Available stock exceeded';
  var STORE_KEY = 'el-cart-zip';

  /* ---------------- shared helpers ---------------- */
  function cleanText(v) {
    if (v == null) return '';
    if (typeof v === 'string') {
      var s = v.replace(/<[^>]*>/g, '').trim();
      return /^(undefined|null|cart error|\[object object\])$/i.test(s) ? '' : s;
    }
    if (typeof v === 'number' || typeof v === 'boolean') return '';
    if (Array.isArray(v)) return v.map(cleanText).filter(Boolean).join(' ');
    if (typeof v === 'object') return Object.keys(v).map(function (k) { return cleanText(v[k]); }).filter(Boolean).join(' ');
    return '';
  }
  function errorText(data) {
    if (!data || typeof data !== 'object') return FALLBACK;
    return cleanText(data.description) || cleanText(data.errors) || cleanText(data.message) || FALLBACK;
  }
  function slotFor(input) {
    var line = input && input.closest('.cart-item');
    return line ? line.querySelector('[data-el-qty-msg]') : null;
  }
  // Clear, then set after a tick, so a repeated identical message is still announced.
  function say(slot, msg) {
    if (!slot) return;
    slot.textContent = '';
    if (msg) setTimeout(function () { slot.textContent = msg; }, 60);
  }
  function maxOf(input) {
    var m = parseInt(input.getAttribute('max'), 10);
    return isNaN(m) ? null : m;
  }
  function stockMsg(input) {
    return input.getAttribute('data-el-stock-msg') || FALLBACK;
  }
  function refreshButtons(input) {
    var qi = input.closest('quantity-input');
    if (qi && typeof qi.validateQtyRules === 'function') qi.validateQtyRules();
  }

  /* ---------------- 1. Stock cap ---------------- */
  var pending = {}; // variant id -> message to announce once Pillar has re-rendered the cart

  document.addEventListener('change', function (e) {
    var input = e.target;
    if (!input || !input.matches || !input.matches('cart-items .quantity__input[data-el-stock]')) return;
    var max = maxOf(input);
    var v = parseInt(input.value, 10);
    if (max === null || isNaN(v)) return;
    var slot = slotFor(input);
    var vid = input.getAttribute('data-quantity-variant-id');
    if (v < max) {
      say(slot, '');
      if (vid) delete pending[vid];
      return;
    }
    var msg = stockMsg(input);
    if (v > max) input.value = max;
    if (String(max) === input.defaultValue) {
      // Already holding every unit in stock: nothing to send. Stop Pillar's handler and announce now.
      e.stopImmediatePropagation();
      refreshButtons(input);
      say(slot, msg);
      return;
    }
    // The change goes through (Pillar re-renders the line); show now, announce again in the new markup.
    if (slot) slot.textContent = msg;
    if (vid) pending[vid] = msg;
  }, true);

  function flushPending(root) {
    Object.keys(pending).forEach(function (vid) {
      var input = (root || document).querySelector('cart-items .quantity__input[data-quantity-variant-id="' + vid + '"]');
      if (!input) return;
      say(slotFor(input), pending[vid]);
      delete pending[vid];
    });
  }

  // Inline message instead of Pillar's native validity bubble (and never "undefined").
  function patchCartItems() {
    var C = window.customElements && customElements.get('cart-items');
    if (!C || C.prototype.__elPatched) return;
    var original = C.prototype.setValidity;
    C.prototype.setValidity = function (target, message) {
      if (!target || !target.closest) return original ? original.apply(this, arguments) : undefined;
      try { target.setCustomValidity(''); } catch (err) { /* not a form control */ }
      target.value = target.defaultValue; // back to what the cart really holds
      refreshButtons(target);
      var slot = slotFor(target);
      var text = cleanText(message) || FALLBACK;
      if (slot) say(slot, text);
      else if (original) original.call(this, target, text);
    };
    C.prototype.__elPatched = true;
  }
  if (window.customElements) customElements.whenDefined('cart-items').then(patchCartItems);

  function changeUrl() {
    return (window.FoxTheme && FoxTheme.routes && FoxTheme.routes.cart_change_url) || '/cart/change';
  }
  function isChange(input) {
    try {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var path = new URL(url, location.origin).pathname.replace(/\.js$/, '');
      return path === changeUrl().replace(/\.js$/, '') || /\/cart\/change$/.test(path);
    } catch (err) { return false; }
  }
  function parseBody(init) {
    try {
      var b = init && init.body;
      if (typeof b === 'string') return JSON.parse(b);
      if (b && typeof b.get === 'function') return { line: b.get('line'), quantity: b.get('quantity'), id: b.get('id') };
    } catch (err) { /* not JSON */ }
    return null;
  }

  var nativeFetch = window.fetch;
  if (typeof nativeFetch === 'function') {
    window.fetch = function (input, init) {
      var p = nativeFetch.apply(this, arguments);
      if (!isChange(input)) return p;
      var req = parseBody(init);
      return p.then(function (res) {
        return res.clone().json().then(function (data) {
          var refused = !res.ok || !data || (!data.items && (data.status || data.errors || data.description));
          if (refused) {
            var fixed = {};
            if (data && typeof data === 'object') Object.keys(data).forEach(function (k) { fixed[k] = data[k]; });
            fixed.errors = errorText(data);
            // The line keeps its old quantity: drop any queued "Only N available" for it.
            var refusedLine = req && parseInt(req.line, 10);
            var lineInput = refusedLine > 0 && document.querySelector('cart-items .quantity__input[data-index="' + refusedLine + '"]');
            if (lineInput) delete pending[lineInput.getAttribute('data-quantity-variant-id')];
            return new Response(JSON.stringify(fixed), {
              status: res.status,
              statusText: res.statusText,
              headers: { 'Content-Type': 'application/json' }
            });
          }
          // Accepted but trimmed to available stock: announce after the re-render.
          var line = req && parseInt(req.line, 10);
          var qty = req && parseInt(req.quantity, 10);
          if (line > 0 && qty > 0 && data.items && data.items[line - 1]) {
            var item = data.items[line - 1];
            if (item.quantity < qty) {
              var el = document.querySelector('cart-items .quantity__input[data-quantity-variant-id="' + item.variant_id + '"]');
              pending[item.variant_id] = (el && el.getAttribute('data-el-stock-msg')) ||
                ('Only ' + item.quantity + ' available' + (item.product_has_only_default_variant ? '' : ' in this size'));
            }
          }
          return res;
        }, function () { return res; }); // non-JSON body: leave it to the theme
      });
    };
  }

  /* ---------------- 2. Delivery estimator ---------------- */
  var lastZip = '';
  var estimated = false; // the shopper (or a prefill) already asked for an estimate this session
  try {
    lastZip = sessionStorage.getItem(STORE_KEY) || '';
    estimated = !!lastZip;
  } catch (err) { /* storage blocked */ }
  function remember(zip) {
    lastZip = zip;
    estimated = true;
    try { sessionStorage.setItem(STORE_KEY, zip); } catch (err) { /* storage blocked */ }
  }

  var loader = null;
  function ready() {
    return typeof window.elZipGroundDays === 'function' && typeof window.elShipMath === 'function';
  }
  function loadZipData(box) {
    if (ready()) return Promise.resolve(true);
    if (loader) return loader;
    var src = box && box.getAttribute('data-src');
    if (!src) return Promise.resolve(false);
    loader = new Promise(function (resolve) {
      var s = document.createElement('script');
      s.src = src;
      s.async = true;
      s.onload = function () { resolve(ready()); };
      s.onerror = function () { loader = null; resolve(false); };
      document.head.appendChild(s);
    });
    return loader;
  }

  function fmt(d) { return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' }); }
  function node(tag, cls, text) { var n = document.createElement(tag); if (cls) n.className = cls; if (text != null) n.textContent = text; return n; }

  // Pure: the estimate as data, for rendering and for the Node test harness.
  function buildEstimate(zip, groundDaysFor, math, backorderDays, compact) {
    if (!/^\d{5}$/.test(zip)) return { error: 'Please enter a valid 5-digit ZIP code.' };
    var ground = groundDaysFor(zip);
    if (!ground) return { error: 'Shipping data is unavailable for ZIP prefix ' + zip.substring(0, 3) + '. Please call ' + ((window.elStore && window.elStore.phone) || '253-581-3220') + ' for a quote.' }; // EL 2026-10-02: phone from Store info (window.elStore)
    var est = math.estimate(ground, { backorderDays: backorderDays || 0 });
    var services = est.services;
    if (compact) {
      // Drawer: Ground plus the fastest option only.
      var fastest = services.filter(function (s) { return s.tag === 'Fastest'; })[0];
      services = [services[0]];
      if (fastest && fastest !== est.services[0]) services.push(fastest);
    }
    return {
      ship: est.ship,
      services: services.map(function (s) {
        return { name: s.name, tag: compact ? '' : s.tag, days: s.days, date: fmt(s.date) };
      }),
      fine: 'Ships from our Lakewood store ' + fmt(est.ship) +
        (backorderDays ? ' (includes a ' + backorderDays + '-day backorder delay for items not in stock)' : '') +
        '. Carrier transit counts business days (Mon–Fri).'
    };
  }

  function render(box, result) {
    var input = box.querySelector('[data-el-cart-zip-input]');
    var errorEl = box.querySelector('[data-el-cart-zip-error]');
    var results = box.querySelector('[data-el-cart-zip-results]');
    if (!results || !errorEl) return;
    errorEl.textContent = '';
    if (input) input.removeAttribute('aria-invalid');
    results.textContent = '';
    if (result.error) {
      errorEl.textContent = result.error;
      if (input) input.setAttribute('aria-invalid', 'true');
      return;
    }
    var list = node('ul', 'el-cart-zip__list');
    result.services.forEach(function (x) {
      var li = node('li', 'el-cart-zip__method');
      li.appendChild(node('span', 'el-cart-zip__name font-body-bolder', x.name));
      if (x.tag) li.appendChild(node('span', 'el-cart-zip__tag', x.tag));
      var when = node('span', 'el-cart-zip__date');
      when.appendChild(document.createTextNode('Get it by '));
      when.appendChild(node('strong', '', x.date));
      li.appendChild(when);
      if (box.getAttribute('data-context') !== 'drawer') {
        li.appendChild(node('span', 'el-cart-zip__detail text-subtext', x.days + ' business day' + (x.days !== 1 ? 's' : '') + ' in transit'));
      }
      list.appendChild(li);
    });
    results.appendChild(list);
    results.appendChild(node('p', 'el-cart-zip__fine text-subtext', result.fine));
  }

  function estimate(box, zip, opts) {
    opts = opts || {};
    var results = box.querySelector('[data-el-cart-zip-results]');
    if (!/^\d{5}$/.test(zip)) {
      render(box, { error: 'Please enter a valid 5-digit ZIP code.' });
      if (opts.focusOnError) { var i = box.querySelector('[data-el-cart-zip-input]'); if (i) i.focus(); }
      return;
    }
    if (results && !ready()) results.textContent = 'Calculating…';
    loadZipData(box).then(function (ok) {
      if (!box.isConnected) return;
      if (!ok) {
        render(box, { error: 'Delivery estimates are unavailable right now. Please call ' + ((window.elStore && window.elStore.phone) || '253-581-3220') + '.' }); // EL 2026-10-02: phone from Store info
        return;
      }
      var math = window.elShipMath(window.elShipping || {});
      var backorder = parseInt(box.getAttribute('data-backorder-days'), 10) || 0;
      var result = buildEstimate(zip, window.elZipGroundDays, math, backorder, box.getAttribute('data-context') === 'drawer');
      render(box, result);
      if (!result.error) remember(zip);
      else if (opts.focusOnError) { var inp = box.querySelector('[data-el-cart-zip-input]'); if (inp) inp.focus(); }
    });
  }

  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || !form.matches || !form.matches('[data-el-cart-zip-form]')) return;
    e.preventDefault();
    var box = form.closest('[data-el-cart-zip]');
    var input = form.querySelector('[data-el-cart-zip-input]');
    if (box && input) estimate(box, input.value.trim(), { focusOnError: true });
  });

  var io = 'IntersectionObserver' in window ? new IntersectionObserver(function (entries) {
    entries.forEach(function (en) {
      if (!en.isIntersecting) return;
      io.unobserve(en.target);
      var input = en.target.querySelector('[data-el-cart-zip-input]');
      if (input && /^\d{5}$/.test(input.value.trim())) estimate(en.target, input.value.trim());
    });
  }) : null;

  function hydrate(box) {
    if (box.__elHydrated) return;
    box.__elHydrated = true;
    var input = box.querySelector('[data-el-cart-zip-input]');
    if (!input) return;
    var prefill = box.getAttribute('data-prefill') || '';
    // A ZIP the shopper typed this session wins over the account address.
    var zip = (estimated && lastZip) || prefill;
    if (!zip) return;
    input.value = zip;
    if (ready()) { estimate(box, zip); return; }   // script already here: no request
    if (io) io.observe(box);                       // fetch the ZIP data only once the box is on screen
  }

  /* ---------- 3. Ship-today line + pickup readiness (EL 2026-10-10) ---------- */
  // [data-el-cart-eta]: "Order within 2h 14m to ship today" before the cutoff, else elShipMath().eta() ("Ships tomorrow").
  // [data-el-cart-pickup]: "Free pickup in Lakewood, ready within 2 hours" / "..., ready when we open tomorrow at 10 AM".
  var cartMath = null;
  function shipMath() {
    if (!cartMath && typeof window.elShipMath === 'function') cartMath = window.elShipMath(window.elShipping || {});
    return cartMath;
  }
  function updateEta(root) {
    var m = shipMath();
    var r = root || document;
    if (!m || !r.querySelectorAll) return;
    r.querySelectorAll('[data-el-cart-eta]').forEach(function (p) {
      var left = m.minutesToCutoff();
      if (left > 0) {
        var h = Math.floor(left / 60), mm = left % 60;
        p.textContent = 'Order within ' + (h > 0 ? h + 'h ' + mm + 'm' : (mm > 1 ? mm + ' min' : 'less than a minute')) + ' to ship today';
      } else {
        p.textContent = m.eta();
      }
      p.hidden = !p.textContent;
    });
    if (typeof m.pickupStatus !== 'function') return;
    r.querySelectorAll('[data-el-cart-pickup]').forEach(function (p) {
      var base = p.getAttribute('data-base') || '';
      if (base) p.textContent = base + ', ' + m.pickupStatus(undefined, (window.elShipping && window.elShipping.pickupReady) || 'within 2 hours');
    });
  }

  function scan(root) {
    var r = root || document;
    if (r.matches && r.matches('[data-el-cart-zip]')) hydrate(r);
    if (r.querySelectorAll) r.querySelectorAll('[data-el-cart-zip]').forEach(hydrate);
    if (r.querySelector && ((r.matches && r.matches('[data-el-cart-eta], [data-el-cart-pickup]')) || r.querySelector('[data-el-cart-eta], [data-el-cart-pickup]'))) updateEta(r.parentNode || r);
  }

  function init() {
    patchCartItems();
    scan(document);
    updateEta(document);
    setInterval(function () { updateEta(document); }, 30000);
    new MutationObserver(function (muts) {
      var sawLine = false;
      for (var i = 0; i < muts.length; i++) {
        var added = muts[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var n = added[j];
          if (n.nodeType !== 1) continue;
          scan(n);
          if (!sawLine && (n.matches('[data-el-qty-msg], .cart-item, cart-items') || n.querySelector('[data-el-qty-msg]'))) sawLine = true;
        }
      }
      if (sawLine && Object.keys(pending).length) flushPending();
    }).observe(document.body, { childList: true, subtree: true });
  }

  window.elCart = { cleanText: cleanText, errorText: errorText, buildEstimate: buildEstimate }; // test hooks
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
